import { IScheduledJob } from "@kuika/kuika-cl-model";
import { ReCron } from "@sbzen/re-cron";
import { Form, FormInstance } from "antd/es";
import * as React from "react";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaListPage from "../../ui-kit/kuika-list-page/kuika-list-page";
import { KuikaListTableRowAction } from "../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaCodeText from "../../ui-kit/typography/CodeText/CodeText";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import { ScheduledJobEditorHeader } from "./scheduled-job-editor-header";
import { describeCronValue } from "./scheduled-job-templates";
import "./cron-maker.scss";
import "./scheduled-jobs.scss";
import { DEFAULT_TIME_ZONE, describeTimeZone, TIME_ZONE_OPTIONS } from "./scheduled-job-time-zones";

/**
 * The job editor: what this job is called, when it runs, and in which timezone. The work
 * it runs is attached in the right panel, which needs a saved job to bind to.
 *
 * The cron builder is `@sbzen/re-cron` and stays that way — it already does the half that
 * matters, dropdowns that cannot express an invalid value. What it never did is say what
 * the expression MEANS, so the summary band above it reads the schedule back as a
 * sentence, live, while you build it.
 */

export interface IScheduledJobEditorFormValues {
  name: string;
  timeZone: string;
}

export interface IScheduledJobEditorProps {
  job: IScheduledJob;
  /** Every job in the project, for the header's switcher. */
  jobs: IScheduledJob[];
  /**
   * Where the header's UPDATE stands. The save used to dim the whole editor behind a spinner;
   * the lock is the button's own now (it refuses a click while it says "Saving"), and nothing
   * here loads over the network — the job arrives with the canvas.
   */
  saveStatus: KuikaButtonStatus;
  /** Resolves once the write has landed, so the editor can re-baseline its dirty check. */
  onUpdate: (values: IScheduledJobEditorFormValues, cronValue: string) => Promise<void>;
  onSwitchJob: (job: IScheduledJob) => void;
  onCreateNew: () => void;
  /** Leaving the editor; the host runs the unsaved-changes handshake. */
  onRequestClose: () => void;
  /** The job list's own row actions, for the switcher's "…". */
  rowActions: KuikaListTableRowAction<IScheduledJob>[];
  /** Reports the current form state so the host can answer "is this dirty?". */
  registerDirtyProbe: (probe: () => IScheduledJobEditorSnapshot) => void;
}

/** Everything the dirty check compares. */
export interface IScheduledJobEditorSnapshot {
  name: string;
  cronValue: string;
  timeZone: string;
}

interface IScheduledJobEditorState {
  cronValue: string;
  /** Mirrors the form's timezone so the summary band can name it. */
  timeZone: string;
  isSpecificCronValueSelected: boolean;
  activeTab: string;
  isNameOnEdit: boolean;
  jobName: string;
}

export class ScheduledJobEditor extends React.PureComponent<IScheduledJobEditorProps, IScheduledJobEditorState> {
  formRef = React.createRef<FormInstance>();

  constructor(props: IScheduledJobEditorProps) {
    super(props);
    this.state = {
      cronValue: props.job.json?.cronValue ?? "",
      timeZone: props.job.json?.timeZone || DEFAULT_TIME_ZONE,
      isSpecificCronValueSelected: false,
      activeTab: "",
      isNameOnEdit: false,
      jobName: props.job.name
    };
  }

  componentDidMount(): void {
    this.props.registerDirtyProbe(this.getSnapshot);
    this.seedFromJob();
  }

  componentDidUpdate(prevProps: IScheduledJobEditorProps): void {
    if (prevProps.job.id !== this.props.job.id) {
      this.seedFromJob();
    }
  }

  seedFromJob = (): void => {
    const job: IScheduledJob = this.props.job;
    const cronValue: string = job.json?.cronValue ?? "";
    this.formRef.current?.resetFields();
    this.formRef.current?.setFieldsValue({
      name: job.name || "",
      timeZone: job.json?.timeZone || DEFAULT_TIME_ZONE
    });
    this.setState({
      isNameOnEdit: false,
      jobName: job.name,
      cronValue,
      timeZone: job.json?.timeZone || DEFAULT_TIME_ZONE
    });
    this.setSpecificCronValueVisibilityForEachTab(cronValue, "SECONDS");
  };

  getSnapshot = (): IScheduledJobEditorSnapshot => ({
    name: this.state.jobName,
    cronValue: this.state.cronValue,
    timeZone: this.formRef.current?.getFieldValue("timeZone")
  });

  handleValuesChange = (changed: Partial<IScheduledJobEditorFormValues>): void => {
    if (changed.timeZone) {
      this.setState({ timeZone: changed.timeZone });
    }
  };

  handleCronChange = (cronValue: string): void => {
    this.setState({ cronValue });
    this.setSpecificCronValueVisibilityForEachTab(
      cronValue,
      this.state.activeTab !== "" ? this.state.activeTab : "SECONDS"
    );
  };

  handleTabChange = (activeTab: string): void => {
    this.setState({ activeTab });
    this.setSpecificCronValueVisibilityForEachTab(this.state.cronValue, activeTab);
  };

  /**
   * Which field of the expression the open tab edits. The builder hides its "specific
   * values" grid unless that field actually holds a list, and it reports the tab by name
   * rather than by index, so the mapping lives here.
   */
  setSpecificCronValueVisibilityForEachTab = (cronValue: string, activeTab: string): void => {
    if (!cronValue) return;
    const fields: string[] = cronValue.split(" ");
    const indexByTab: Record<string, number> = {
      SECONDS: 0,
      MINUTES: 1,
      HOURS: 2,
      DAY: 3,
      MONTH: 4,
      YEAR: 6
    };
    const index: number | undefined = indexByTab[activeTab];
    if (index === undefined) return;
    this.setSpecificCronValueWithTab(fields[index]);
  };

  setSpecificCronValueWithTab = (field?: string): void => {
    const isSpecificCronValueSelected: boolean =
      !!field &&
      !field.includes("*") &&
      !field.includes("/") &&
      !field.includes("-") &&
      !field.includes("?") &&
      !/\bL\b/.test(field) &&
      !/\bW\b/.test(field);
    this.setState({ isSpecificCronValueSelected });
  };

  /**
   * Case-insensitive, and deliberately NOT locale-aware: `toLowerCase` is kept exactly as
   * the pre-refactor validator had it, so which duplicates get rejected does not shift for
   * Turkish-named projects. The job being edited is excluded from its own check.
   */
  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const jobs: IScheduledJob[] = this.props.jobs ?? [];
    if (jobs.length === 0) return;
    if (
      jobs.some(
        (job: IScheduledJob): boolean => job.name.toLowerCase() === value.toLowerCase() && job.id !== this.props.job.id
      )
    ) {
      throw new Error("This Scheduled Job Name is used before.");
    }
  };

  validateName = async (): Promise<string | undefined> => {
    try {
      const values: IScheduledJobEditorFormValues = await this.formRef.current?.validateFields(["name"]);
      return values?.name;
    } catch {
      return undefined;
    }
  };

  handleSubmitName = (name: string): void => {
    this.setState({ isNameOnEdit: false, jobName: name });
  };

  handleAbortRename = (): void => {
    this.formRef.current?.setFieldsValue({ name: this.state.jobName });
    this.setState({ isNameOnEdit: false });
  };

  handleFinish = async (values: IScheduledJobEditorFormValues): Promise<void> => {
    // The header's rename commits to `jobName`; the form field is the live input, which
    // is still whatever the user last typed if they never confirmed it.
    await this.props.onUpdate({ name: this.state.jobName, timeZone: values.timeZone }, this.state.cronValue);
  };

  render() {
    const scheduleSentence: string | undefined = describeCronValue(this.state.cronValue);
    const timeZoneLabel: string | undefined = describeTimeZone(this.state.timeZone);

    return (
      <Form
        name="scheduled-jobs"
        ref={this.formRef}
        onFinish={this.handleFinish}
        onValuesChange={this.handleValuesChange}
        className="kuika-scheduled-job-editor__form"
        initialValues={{
          name: this.props.job.name,
          timeZone: this.props.job.json?.timeZone || DEFAULT_TIME_ZONE
        }}
      >
        {/* The bar is the column's first row, OUTSIDE the page's own gutter — it is the
            editor's chrome and does not scroll with what it heads. */}
        <ScheduledJobEditorHeader
          jobName={this.state.jobName}
          isNameOnEdit={this.state.isNameOnEdit}
          onStartRename={() => this.setState({ isNameOnEdit: true })}
          onSubmitName={this.handleSubmitName}
          onAbortRename={this.handleAbortRename}
          nameValidator={this.existingNameValidator}
          validateName={this.validateName}
          jobs={this.props.jobs}
          currentJob={this.props.job}
          onSwitchJob={this.props.onSwitchJob}
          onCreateNew={this.props.onCreateNew}
          onBack={this.props.onRequestClose}
          rowActions={this.props.rowActions}
          onUpdate={() => this.formRef.current?.submit()}
          updateStatus={this.props.saveStatus}
        />

        <KuikaListPage className="kuika-scheduled-job-editor" data-testid="scheduledJobEditorScreen" belowBar>
          <div className="kuika-scheduled-job-editor__body">
            {/* The expression read back as a sentence. This is the control that makes
                the builder below legible — the raw form is kept beside it, demoted,
                because seeing both is how the syntax gets learned. */}
            <div className="kuika-scheduled-job-editor__summary">
              <span className="kuika-scheduled-job-editor__summary-icon">
                <KuikaSvg name="clock" width={16} height={16} />
              </span>
              <span className="kuika-scheduled-job-editor__summary-text">
                <KuikaTextMedium className="kuika-scheduled-job-editor__summary-sentence">
                  {scheduleSentence ?? "Pick a schedule below"}
                </KuikaTextMedium>
                <KuikaSmallText className="kuika-scheduled-job-editor__summary-caption">
                  {/* Naming the timezone is the difference between a schedule you can
                      check and one you have to scroll down to finish reading — and it is
                      named by its offset, the way the picker below labels it, because
                      "Europe/London" does not answer "when". */}
                  {timeZoneLabel ? `Runs in ${timeZoneLabel}.` : "Pick a timezone below."}
                </KuikaSmallText>
              </span>
              <KuikaCodeText className="kuika-scheduled-job-editor__summary-cron" data-testid="resultInput">
                {this.state.cronValue}
              </KuikaCodeText>
            </div>

            <div className="kuika-scheduled-job-editor__field">
              <span className="kuika-scheduled-job-editor__label">Timezone</span>
              <Form.Item name="timeZone" rules={[{ required: true, message: "Please select a timezone" }]}>
                <KuikaSelectbox
                  data-testid="timezoneDropdown"
                  size="md"
                  searchable
                  placeholder="Select a timezone"
                  options={TIME_ZONE_OPTIONS}
                />
              </Form.Item>
            </div>

            <div className="kuika-scheduled-job-editor__field">
              <span className="kuika-scheduled-job-editor__label" data-testid="settingsTitle">
                Schedule
              </span>
              <div
                className={
                  this.state.isSpecificCronValueSelected
                    ? "kuika__cron-maker"
                    : "kuika__cron-maker kuika__cron-maker__specific-not-selected"
                }
              >
                <div
                  className={
                    this.state.activeTab === "MONTH" || this.state.activeTab === "DAY"
                      ? "kuika__cron-maker__long-select"
                      : ""
                  }
                >
                  <ReCron
                    value={this.state.cronValue}
                    onChange={this.handleCronChange}
                    onTabChange={this.handleTabChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </KuikaListPage>
      </Form>
    );
  }
}
